import { useQuery } from "@tanstack/react-query";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { config } from "@/config";
import { StepItem } from "./use-get-functional-graph-service";
import { apiFetch } from "@/lib/api-client";

interface GetNodesV2Params {
  projectUuid: string; 
  nodeType: "Step" | "Persona" | "Scenario" | "Action" | "Api" | "Outcome";
  filters?: Record<string, any>;
  limit?: number;
  page?: number;
  enabled?: boolean;
}

interface GetNodesV2Response {
  data: StepItem[];
  total: number;
  page: number;
  totalPages: number;
}

const getNodesV2 = async ({
  projectUuid,
  nodeType,
  filters,
  page = 1,
  limit = 10,
  headers,
}: GetNodesV2Params & {
  headers: Record<string, string>;
}): Promise<GetNodesV2Response> => {
  // Build URL manually to avoid encoding square brackets
  let url = `${config.isometricApiUrl}/functional-graph/v2/get-nodes/${nodeType}`;

  // Scope the request to the project (the backend requires projectUuid), with
  // unencoded square brackets
  url += `?filters[projectUuid][$eq]=${projectUuid}`;

  // Add pagination parameters
  url += `&page=${page}&limit=${limit}`;

  // Add additional filters if needed (optional)
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        Object.entries(value).forEach(([operator, operand]) => {
          url += `&filters[${key}][${operator}]=${operand}`;
        });
      } else {
        url += `&filters[${key}][$eq]=${value}`;
      }
    });
  }

  const response = await apiFetch(url, {
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${nodeType} nodes: ${response.statusText}`);
  }

  const result = await response.json();
  return {
    data: result.data || [],
    total: result.total || 0,
    page: page,
    totalPages: Math.ceil((result.total || 0) / limit),
  };
};

export const useGetNodesV2Service = ({
  projectUuid,
  nodeType,
  filters,
  limit = 10,
  page = 1,
  enabled = true,
}: GetNodesV2Params) => {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: ["get-nodes-v2", projectUuid, nodeType, filters, page, limit],
    queryFn: () =>
      getNodesV2({
        projectUuid,
        nodeType,
        filters,
        page,
        limit,
        headers: getAuthHeaders(),
      }),
    enabled: enabled && !!projectUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};